import { AppleIDAuthResponse } from './types/appleAuth';
import { AppleSocialIconSmall } from '@liveauctioneers/hammer-ui-core/icons/social/appleSocialIcon';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useHandleAppleReauth } from './hooks/useHandleAppleReauth';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useState } from 'react';

type Props = {
    className?: string;
    onFailure?: (error: any) => void;
    onSuccess?: (response: AppleIDAuthResponse) => void;
};

/**
 * Apple Reauthentication Component
 *
 * This component provides a custom Apple Sign In button for reauthentication scenarios,
 * particularly needed for sensitive operations like account deletion. Unlike the standard
 * Apple Sign In flow, this component:
 *
 * 1. Uses a custom Button component instead of Apple's default button to maintain UI consistency
 * 2. Manually triggers Apple's authentication flow via `window.AppleID.auth.signIn()`
 * 3. Includes a hidden Apple Sign In div that's required by Apple's SDK but not visually displayed
 * 4. Handles loading states during the authentication process
 *
 * The hidden div with id="appleid-signin" is required by Apple's JavaScript SDK to function
 * properly, even though we're using our custom button for the actual user interaction.
 *
 * @param className - Optional CSS class for styling the container
 * @param onFailure - Callback function called when authentication fails
 * @param onSuccess - Callback function called when authentication succeeds
 */
export const AppleReauth = ({ className, onFailure, onSuccess }: Props) => {
    const { formatMessage } = useIntl();
    const [isLoading, setIsLoading] = useState(false);
    const handleSuccess = (response: AppleIDAuthResponse) => {
        setIsLoading(false);
        onSuccess?.(response);
    };

    const handleFailure = (error: any) => {
        setIsLoading(false);
        onFailure?.(error);
    };

    const handleButtonClick = async () => {
        setIsLoading(true);

        try {
            // Manually trigger Apple's authentication flow
            // This bypasses Apple's default button styling while still using their SDK
            await window.AppleID.auth.signIn();
        } catch (error) {
            console.error({ error });
        }
    };

    useHandleAppleReauth({ onFailure: handleFailure, onSuccess: handleSuccess });

    return (
        <div className={className}>
            <Button
                className="mt-sm w-full"
                // @ts-expect-error this works, but should be fixed in the future.
                iconLeft={isLoading ? null : AppleSocialIconSmall}
                loading={isLoading}
                onClick={handleButtonClick}
                style="neutral"
                variant="outlined"
            >
                {formatMessage({ id: 'loginModal.continueWithApple' })}
            </Button>
            {/* Hidden Apple Sign In div required by Apple's SDK for authentication to work
                This div must exist in the DOM but is hidden since we use our custom button */}
            <div
                data-border="true"
                data-color="black"
                data-type="sign in"
                id="appleid-signin"
                style={{ display: 'none' }}
            />
        </div>
    );
};
